import React, { useState } from "react";
import { Archive, ArrowUp, CircleCheck, CopyPlus, Loader, MessageSquareText, Pencil, Play, Send, ThumbsDown, ThumbsUp, Undo2, UserCheck, X } from "lucide-react";
import { toast } from "react-toastify";
import api from "~/services/api";
import { Attachments, ResolvedAtField, WorkedField, buildFormData, errorMessage, nowLocal, splitMinutes, elapsedMinutes, toMinutes, useFileDrop } from "~/components/SupportChat/tickets";

const TONES = {
  green: "bg-green-600 text-white border-green-600",
  red: "bg-white text-red-600 border-red-300 hover:bg-red-50",
  amber: "bg-white text-amber-600 border-amber-300 hover:bg-amber-50",
  primary: "bg-primary text-white border-primary",
  plain: "bg-secondary text-gray-700 hover:border-primary",
};

const PANELS = {
  rejected: { title: "Reject", url: "decision", fields: { decision: "rejected" }, key: "reason", required: true, files: true },
  changes_requested: { title: "Request changes", url: "decision", fields: { decision: "changes_requested" }, key: "reason", required: true, files: true },
  version: { title: "New version", url: "versions", fields: {}, key: "description", required: true, files: true, why: true },
  escalate: { title: "Escalate to the next level", url: "transition", fields: { action: "escalate" }, key: "note", required: true, json: true },
  return: { title: "Return to the previous level", url: "transition", fields: { action: "return" }, key: "note", required: true, json: true },
  closeQuick: { title: "Resolved without problems", url: "transition", fields: { action: "close" }, key: "note", required: false, json: true, time: true, hideText: true },
  close: { title: "Close with a message", url: "transition", fields: { action: "close" }, key: "note", required: false, json: true, time: true },
};

export default function Actions({ ticket, onDone }) {
  const { actions } = ticket;
  const [panel, setPanel] = useState(null);
  const [text, setText] = useState("");
  const [why, setWhy] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resolvedAt, setResolvedAt] = useState(nowLocal);
  const [worked, setWorked] = useState({ hours: "", minutes: "" });
  const drop = useFileDrop(setFiles);

  const reset = () => { setPanel(null); setText(""); setWhy(""); setFiles([]); setResolvedAt(nowLocal()); setWorked({ hours: "", minutes: "" }); };

  const resolveNow = () => {
    const now = nowLocal();
    setResolvedAt(now);
    setWorked(splitMinutes(elapsedMinutes(ticket.created_at, now)));
  };

  async function send(url, body, { json } = {}) {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${ticket.id}/${url}`, json ? body : buildFormData(body, files));
      reset();
      await onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const current = panel && PANELS[panel];
  const confirm = () => {
    const value = text.trim();
    if (current.required && value.length < 3) return toast.error("Write a few words first.");
    const body = { ...current.fields, [current.key]: value, ...(current.why ? { reason: why } : {}) };
    if (current.time) {
      if (Number.isNaN(new Date(resolvedAt).getTime())) return toast.error("Choose when it was resolved.");
      const workMinutes = toMinutes(worked);
      if (workMinutes < 1) return toast.error("Inform the time spent on the solution.");
      if (workMinutes > (new Date(resolvedAt) - new Date(ticket.created_at)) / 60000) return toast.error("The time on the solution cannot be longer than the elapsed time.");
      body.resolved_at = new Date(resolvedAt).toISOString();
      body.work_minutes = workMinutes;
    }
    return send(current.url, body, { json: current.json });
  };

  const buttons = [
    { show: actions.approve, label: "Approve", Icon: ThumbsUp, tone: "green", run: () => send("decision", { decision: "approved" }) },
    { show: actions.approve, label: "Reject", Icon: ThumbsDown, tone: "red", run: () => setPanel("rejected") },
    { show: actions.approve, label: "Request changes", Icon: Pencil, tone: "amber", run: () => setPanel("changes_requested") },
    { show: actions.start, label: "Start work", Icon: Play, tone: "primary", run: () => send("transition", { action: "start" }, { json: true }) },
    { show: actions.assign, label: "Assign to me", Icon: UserCheck, tone: "plain", run: () => send("transition", { action: "assign" }, { json: true }) },
    { show: actions.close, label: "Resolved without problems", Icon: CircleCheck, tone: "green", run: () => { resolveNow(); setPanel("closeQuick"); } },
    { show: actions.close, label: "Close with a message", Icon: MessageSquareText, tone: "plain", run: () => { resolveNow(); setPanel("close"); } },
    { show: actions.escalate, label: `Escalate to N${ticket.level + 1}`, Icon: ArrowUp, tone: "amber", run: () => setPanel("escalate") },
    { show: actions.return, label: `Return to N${ticket.level - 1}`, Icon: Undo2, tone: "plain", run: () => setPanel("return") },
    { show: actions.version, label: "New version", Icon: CopyPlus, tone: "plain", run: () => setPanel("version") },
    { show: actions.archive, label: "Archive", Icon: Archive, tone: "plain", run: () => send("transition", { action: "archive" }, { json: true }) },
  ].filter((b) => b.show);

  if (!buttons.length) return null;

  return (
    <div className="w-full px-4 flex flex-col gap-3">
      {!current && (
        <div className="flex flex-row flex-wrap gap-2">
          {buttons.map(({ label, Icon, tone, run }) => (
            <button key={label} type="button" disabled={busy} onClick={run} className={`flex items-center gap-2 text-xs font-bold border rounded-md px-3 h-8 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${TONES[tone]}`}>
              {busy ? <Loader size={14} className="animate-spin" /> : <Icon size={14} />} {label}
            </button>
          ))}
        </div>
      )}

      {current && (
        <div {...drop.handlers} className="border rounded-lg p-3 flex flex-col gap-2 bg-white" style={{ outline: drop.over ? "2px dashed #ee5827" : "none" }}>
          <div className="flex items-center justify-between text-sm font-bold">
            {current.title}
            <button type="button" onClick={reset} className="p-1 rounded hover:bg-gray-100"><X size={16} /></button>
          </div>
          {current.why && (
            <input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Why a new version?" className="border rounded-md px-3 py-2 text-sm outline-none" />
          )}
          {current.time && (
            <>
              <ResolvedAtField value={resolvedAt} onChange={setResolvedAt} onNow={resolveNow} openedAt={ticket.created_at} />
              <WorkedField value={worked} onChange={setWorked} />
            </>
          )}
          {!current.hideText && <textarea
            autoFocus
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={panel === "escalate" ? "What was tried and why it goes up?" : panel === "return" ? "What does the previous level need to do?" : current.required ? "Write here" : "Message (optional). Leave empty for the default message."}
            className="border rounded-md px-3 py-2 text-sm outline-none resize-none"
          />}
          {current.files && <Attachments files={files} setFiles={setFiles} />}
          <div className="flex justify-end">
            <button type="button" disabled={busy} onClick={confirm} className="flex items-center gap-2 text-xs font-bold bg-primary text-white rounded-md px-3 h-8 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50">
              {busy ? <Loader size={14} className="animate-spin" /> : <Send size={14} />} Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
